import React, { useState, useEffect, useMemo } from 'react';
import { Download, FileText, Calendar, IndianRupee } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';
import SearchableSelect from '../ui/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Build the two halves of a given month
const fortnightRange = (year, month, half) => {
  // half: 1 → 1..15  ·  2 → 16..end-of-month
  const last = new Date(year, month, 0).getDate(); // month here is 1-12
  if (half === 1) {
    return { from: `${year}-${String(month).padStart(2, '0')}-01`, to: `${year}-${String(month).padStart(2, '0')}-15` };
  }
  return { from: `${year}-${String(month).padStart(2, '0')}-16`, to: `${year}-${String(month).padStart(2, '0')}-${last}` };
};

const monthLabel = (year, month) => new Date(year, month - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });

const downloadBlob = async (url, filename, token) => {
  const r = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
  if (!r.ok) {
    alert(`Download failed: ${r.status}`);
    return;
  }
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(u);
};

export default function ClaimReport() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  // Default half = whichever we're in today
  const [half, setHalf] = useState(today.getDate() <= 15 ? 1 : 2);
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const range = useMemo(() => fortnightRange(year, month, half), [year, month, half]);

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/staff`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json();
      setStaffList((data || []).filter(s => s.role === 'staff' || s.role === 'admin'));
    } catch {}
  };

  const fetchReport = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ start_date: range.from, end_date: range.to });
      if (staffId) params.append('staff_id', staffId);
      const r = await fetch(`${API_URL}/api/admin/reports/claim_fortnight?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await r.json();
      setRows(d.rows || []);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [year, month, half, staffId]);

  // Silent 30-sec auto-refresh — no flicker, keeps current filters.
  useAutoRefresh(() => fetchReport(true), 30_000);

  const buildExportUrl = (suffix) => {
    const params = new URLSearchParams({ start_date: range.from, end_date: range.to });
    if (staffId) params.append('staff_id', staffId);
    return `${API_URL}/api/admin/reports/claim_fortnight/${suffix}?${params}`;
  };

  const onExportXlsx = () => {
    const token = localStorage.getItem('token');
    const fn = `carloo_claim_${year}-${String(month).padStart(2, '0')}_H${half}${staffId ? '_filtered' : ''}.xlsx`;
    downloadBlob(buildExportUrl('export'), fn, token);
  };
  const onExportPdf = () => {
    const token = localStorage.getItem('token');
    const fn = `carloo_claim_${year}-${String(month).padStart(2, '0')}_H${half}${staffId ? '_filtered' : ''}.pdf`;
    downloadBlob(buildExportUrl('export-pdf'), fn, token);
  };

  // Helper — treat '', null, undefined and non-numeric strings as 0 so a
  // single bad row doesn't break the totals.
  const numOf = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  // Group rows by Staff for the on-screen view (with per-staff column totals).
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r['Staff ID'] || r['Staff'] || 'Unknown';
      if (!map.has(key)) map.set(key, {
        name: r['Staff'], staff_id: r['Staff ID'], rows: [],
        total_google: 0, total_manual: 0, total_req: 0, total_km: 0, total_claim: 0,
      });
      const g = map.get(key);
      g.rows.push(r);
      g.total_google += numOf(r['Google KM']);
      g.total_manual += numOf(r['Manual KM']);
      g.total_req += numOf(r['Approval Request KM']);
      g.total_km += numOf(r['Approved KM']);
      g.total_claim += numOf(r['Claim Amount']);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Org-wide totals across every staff group.
  const totals = useMemo(() => grouped.reduce((s, g) => ({
    google: s.google + g.total_google,
    manual: s.manual + g.total_manual,
    req: s.req + g.total_req,
    approved: s.approved + g.total_km,
    claim: s.claim + g.total_claim,
  }), { google: 0, manual: 0, req: 0, approved: 0, claim: 0 }), [grouped]);

  const grandTotal = totals.claim;

  // For the year dropdown — last 3 years incl. current
  const years = [today.getFullYear(), today.getFullYear() - 1, today.getFullYear() - 2];

  return (
    <div data-testid="claim-report-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center"><IndianRupee className="mr-2 text-emerald-600" /> Claim Report</h1>
          <p className="text-xs text-gray-500 mt-1">Fortnightly staff-wise visit claim report. Pick a month + half (1–15 or 16–end).</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onExportXlsx}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
            data-testid="claim-export-xlsx"
          >
            <Download size={18} /><span>Excel</span>
          </button>
          <button
            onClick={onExportPdf}
            className="flex items-center space-x-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
            data-testid="claim-export-pdf"
          >
            <FileText size={18} /><span>PDF</span>
          </button>
        </div>
      </div>

      {/* Period picker */}
      <div className="bg-white rounded-xl p-4 shadow-sm border mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1 flex items-center"><Calendar size={12} className="mr-1" />Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm" data-testid="claim-month">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                <option key={m} value={m}>{new Date(year, m - 1, 1).toLocaleString('en-IN', { month: 'long' })}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Year</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2 border rounded-lg text-sm" data-testid="claim-year">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Half</label>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="claim-half-toggle">
              <button
                type="button"
                onClick={() => setHalf(1)}
                className={`px-3 py-2 text-sm ${half === 1 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                data-testid="claim-half-1"
              >1st – 15th</button>
              <button
                type="button"
                onClick={() => setHalf(2)}
                className={`px-3 py-2 text-sm border-l border-gray-200 ${half === 2 ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                data-testid="claim-half-2"
              >16th – End</button>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Staff (optional)</label>
            <SearchableSelect
              value={staffId}
              onChange={setStaffId}
              options={[{ value: '', label: 'All staff' }, ...staffList.map(s => ({
                value: s.id,
                label: `${s.name}`,
                hint: s.staff_id || s.mobile,
              }))]}
              placeholder="All staff"
              testid="claim-staff-filter"
              allowClear
              minWidth={240}
            />
          </div>
          <div className="ml-auto text-xs text-gray-500">
            Period: <strong>{range.from} → {range.to}</strong> · {monthLabel(year, month)}
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="claim-summary">
        <SumCard label="Visits" value={rows.length} tone="blue" />
        <SumCard label="Staff with claims" value={grouped.length} tone="indigo" />
        <SumCard label="Google KM" value={totals.google.toFixed(1)} tone="sky" />
        <SumCard label="Manual KM" value={totals.manual.toFixed(1)} tone="violet" />
        <SumCard label="Approved KM" value={totals.approved.toFixed(1)} tone="amber" />
        <SumCard label="Total Claim" value={`₹${grandTotal.toFixed(2)}`} tone="emerald" />
      </div>

      {/* Per-staff tables */}
      {loading && <div className="text-center py-8 text-gray-500">Loading…</div>}
      {!loading && grouped.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500">No claims for the selected period.</div>
      )}
      <div className="space-y-4">
        {grouped.map(g => (
          <div key={g.staff_id || g.name} className="bg-white rounded-xl shadow-sm overflow-hidden border" data-testid={`claim-group-${g.staff_id || 'unknown'}`}>
            <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">{g.name} <span className="text-xs text-gray-400 font-normal">{g.staff_id}</span></h3>
              <div className="text-xs text-gray-600">
                <span className="mr-3">Approved KM: <strong>{g.total_km.toFixed(1)}</strong></span>
                <span>Total Claim: <strong className="text-emerald-700">₹{g.total_claim.toFixed(2)}</strong></span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Start</th>
                    <th className="px-3 py-2 text-left">End</th>
                    <th className="px-3 py-2 text-right">Google KM</th>
                    <th className="px-3 py-2 text-right">Manual KM</th>
                    <th className="px-3 py-2 text-right">Req KM</th>
                    <th className="px-3 py-2 text-right">Approved KM</th>
                    <th className="px-3 py-2 text-right">Claim</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {g.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2">{r['Date']}</td>
                      <td className="px-3 py-2">{r['Supplier']}</td>
                      <td className="px-3 py-2">{r['Start Time']}</td>
                      <td className="px-3 py-2">{r['End Time']}</td>
                      <td className="px-3 py-2 text-right">{r['Google KM']}</td>
                      <td className="px-3 py-2 text-right">{r['Manual KM']}</td>
                      <td className="px-3 py-2 text-right">{r['Approval Request KM']}</td>
                      <td className="px-3 py-2 text-right">{r['Approved KM'] === '' ? '—' : r['Approved KM']}</td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">₹{r['Claim Amount']}</td>
                      <td className="px-3 py-2 capitalize">{(r['Status'] || '').replaceAll('_', ' ')}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-semibold" data-testid={`claim-staff-total-${g.staff_id || 'unknown'}`}>
                  <tr>
                    <td colSpan="4" className="px-3 py-2 text-right text-gray-700 uppercase text-[10px] tracking-wide">Total ({g.rows.length} visit{g.rows.length === 1 ? '' : 's'})</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.total_google.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.total_manual.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.total_req.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{g.total_km.toFixed(1)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-emerald-700">₹{g.total_claim.toFixed(2)}</td>
                    <td className="px-3 py-2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TONE = {
  blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-900',
  indigo: 'from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-900',
  amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-900',
  emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  sky: 'from-sky-50 to-sky-100 border-sky-200 text-sky-900',
  violet: 'from-violet-50 to-violet-100 border-violet-200 text-violet-900',
};
function SumCard({ label, value, tone }) {
  return (
    <div className={`p-3 rounded-xl border bg-gradient-to-br ${TONE[tone] || TONE.blue}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}
